import type { Exercise } from '../../types';
import { MarkdownText } from '../MarkdownText';
import { CARDS, STATS, INTERACTIVE, LAYOUT } from '../../styles/classNames';

interface ExerciseListProps {
  exercises: Exercise[];
  search: string;
  onSearchChange: (value: string) => void;
  onEdit: (exercise: Exercise) => void;
}

export function ExerciseList({
  exercises,
  search,
  onSearchChange,
  onEdit,
}: ExerciseListProps) {
  return (
    <div className="flex-1 flex flex-col min-h-0">
      <input
        value={search}
        onChange={(e) => onSearchChange(e.target.value)}
        placeholder="Search name or cues"
        className="form-field max-w-none mb-3 shrink-0"
      />
      <div className="space-y-3 flex-1 overflow-auto pr-1 min-h-0">
        {exercises.length === 0 && (
          <p className="text-sm text-slate-400">
            No exercises yet. Add one above or clear your search.
          </p>
        )}
        {exercises.map((exercise) => (
          <div
            key={exercise.id}
            data-testid="exercise-item"
            onClick={() => onEdit(exercise)}
            className={`${CARDS.listItem} ${INTERACTIVE.hoverSubtle} cursor-pointer`}
          >
            <div className="space-y-2">
              {/* Header with name and stats */}
              <div className="flex items-start justify-between gap-2">
                <p className="font-semibold text-sm md:text-base flex-1 min-w-0">{exercise.name}</p>
                <div className={STATS.withDot}>
                  <span className="whitespace-nowrap">
                    {exercise.defaultSets || '—'} x {exercise.defaultReps || '—'}
                  </span>
                  <span className={STATS.dot} />
                  <span className="whitespace-nowrap">{exercise.defaultRestSec || '—'}s</span>
                </div>
              </div>

              {/* Movement pattern and equipment tags */}
              {(exercise.movementPattern || exercise.equipmentTags?.length) && (
                <div className={LAYOUT.row}>
                  {exercise.movementPattern && (
                    <span className={`${STATS.pill} text-primary-600 dark:text-primary-300 bg-primary-50 dark:bg-primary-900/20`}>
                      {exercise.movementPattern.replace('_', ' ')}
                    </span>
                  )}
                  {exercise.equipmentTags?.map((tag) => (
                    <span key={tag} className={`${STATS.pill} text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-white/5`}>
                      {tag}
                    </span>
                  ))}
                </div>
              )}

              {/* Description - hidden on mobile, shown on desktop */}
              <div className="hidden md:block max-h-12 overflow-hidden">
                <MarkdownText
                  content={exercise.description || 'No description yet'}
                  className="text-xs text-gray-600 dark:text-slate-400"
                />
              </div>

              {/* Cues - truncated on mobile */}
              <div className="max-h-12 md:max-h-16 overflow-hidden">
                <MarkdownText
                  content={exercise.defaultCues || 'Add cues to guide your client'}
                  className="text-xs md:text-sm text-gray-700 dark:text-slate-200 line-clamp-2"
                />
              </div>

              {/* Media count */}
              <p className="text-xs text-slate-400">
                {exercise.media?.length || 0} media file{(exercise.media?.length || 0) === 1 ? '' : 's'}
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
